import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Compass } from "lucide-react";
import SessionNavTree from "./SessionNavTree";
import InlineSessionDetail from "./InlineSessionDetail";
import LearningSidebar from "./LearningSidebar";
import { fetchLearningPath, fetchGamification } from "../../../utils/smartLearning";
import "./learningPath.scss";

/**
 * LearningPathView — 3-column student learning workspace.
 *
 * Left:   SessionNavTree   — compact module+session navigation
 * Center: InlineSessionDetail — full session detail panel (replaces offcanvas)
 * Right:  LearningSidebar  — stats, upcoming classes, progress donut, streak
 *
 * A stats row at the top summarises progress across all session types.
 *
 * `aivoOnly` (the student phone and desk shells): "Ask AI Doubt" opens AIvo, or is not
 * there without it. College's My Learning still falls back to Learn with AI.
 */
const LearningPathView = ({ courseId, batchId, courseName, aivoOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  const load = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError("");
    try {
      const [pathData, gamData] = await Promise.all([
        fetchLearningPath({ courseId, batchId }),
        fetchGamification(courseId),
      ]);
      setData(pathData);
      setGamification(gamData);

      // Auto-select next recommended session, falling back to first unlocked
      const allSessions = (pathData.modules || []).flatMap((m) =>
        (m.sessions || []).map((s) => ({
          ...s,
          moduleNumber: m.moduleNumber,
          moduleTitle: m.title,
        })),
      );
      const nextId = pathData.summary?.nextRecommended?.sessionId;
      const autoSelect =
        (nextId && allSessions.find((s) => String(s._id) === String(nextId))) ||
        allSessions.find((s) => s.student?.state !== "locked");
      if (autoSelect) setSelectedSession(autoSelect);
    } catch (err) {
      if (err?.response?.status === 404) {
        setData(null);
      } else {
        setError(
          err?.response?.data?.message || "Failed to load your learning path.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [courseId, batchId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelectSession = useCallback((session) => {
    setSelectedSession(session);
  }, []);

  // ── Stats computation ────────────────────────────────────────────────
  const allSessions = useMemo(
    () => (data?.modules || []).flatMap((m) => m.sessions || []),
    [data],
  );

  const stats = useMemo(() => {
    const totalClasses = data?.summary?.totalSessions || 0;
    const doneClasses =
      (data?.summary?.completedSessions || 0) +
      (data?.summary?.masteredSessions || 0);
    const overall = data?.summary?.overallProgressPercent || 0;
    const totalAssignments = allSessions.reduce(
      (acc, s) => acc + (s.resourceCounts?.assignment || 0),
      0,
    );
    const doneAssignments = allSessions.filter(
      (s) => s.student?.assignmentCompleted,
    ).length;
    const totalCoding = allSessions.reduce(
      (acc, s) => acc + (s.resourceCounts?.codingChallenge || 0),
      0,
    );
    const doneCoding = allSessions.filter((s) => s.student?.codingCompleted)
      .length;
    return {
      overall,
      totalClasses,
      doneClasses,
      totalAssignments,
      doneAssignments,
      totalCoding,
      doneCoding,
    };
  }, [data, allSessions]);

  // ── Render ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" size="sm" />
        <span>Loading your learning path…</span>
      </div>
    );
  }

  if (error) return <div className="lp-error">{error}</div>;

  if (!data || (data.modules || []).length === 0) {
    return (
      <div className="lp-empty">
        <div className="lp-empty__icon">
          <Compass size={28} />
        </div>
        <h3 className="lp-empty__title">Your learning path is being prepared</h3>
        <p className="lp-empty__desc">
          Once your trainer adds sessions to this course, you'll see a guided
          roadmap here with locked and unlocked milestones.
        </p>
      </div>
    );
  }

  const classesPct = stats.totalClasses
    ? Math.round((stats.doneClasses / stats.totalClasses) * 100)
    : 0;
  const assignPct = stats.totalAssignments
    ? Math.round((stats.doneAssignments / stats.totalAssignments) * 100)
    : 0;
  const codingPct = stats.totalCoding
    ? Math.round((stats.doneCoding / stats.totalCoding) * 100)
    : 0;

  return (
    <div className="lp-workspace-root">
      {/* ── Stats row ───────────────────────────────────────────────── */}
      <div className="lp-stats">
        <div className="lp-stats__card">
          <div className="lp-stats__icon-wrap lp-stats__icon-wrap--violet">
            <span>📊</span>
          </div>
          <div className="lp-stats__body">
            <span className="lp-stats__label">Overall Progress</span>
            <span className="lp-stats__num">{stats.overall}%</span>
            <div className="lp-stats__bar">
              <div
                className="lp-stats__fill lp-stats__fill--violet"
                style={{ width: `${stats.overall}%` }}
              />
            </div>
            <span className="lp-stats__hint">
              {stats.overall >= 80
                ? "Amazing work!"
                : stats.overall >= 40
                ? "Keep learning! You're doing great."
                : "Every session counts — let's go!"}
            </span>
          </div>
        </div>

        <div className="lp-stats__card">
          <div className="lp-stats__icon-wrap lp-stats__icon-wrap--green">
            <span>🎓</span>
          </div>
          <div className="lp-stats__body">
            <span className="lp-stats__label">Classes Completed</span>
            <span className="lp-stats__num">
              {stats.doneClasses} / {stats.totalClasses}
            </span>
            <div className="lp-stats__bar">
              <div
                className="lp-stats__fill lp-stats__fill--green"
                style={{ width: `${classesPct}%` }}
              />
            </div>
            <span className="lp-stats__hint">{classesPct}%</span>
          </div>
        </div>

        <div className="lp-stats__card">
          <div className="lp-stats__icon-wrap lp-stats__icon-wrap--orange">
            <span>📝</span>
          </div>
          <div className="lp-stats__body">
            <span className="lp-stats__label">Assignments Done</span>
            <span className="lp-stats__num">
              {stats.doneAssignments} / {stats.totalAssignments}
            </span>
            <div className="lp-stats__bar">
              <div
                className="lp-stats__fill lp-stats__fill--orange"
                style={{ width: `${assignPct}%` }}
              />
            </div>
            <span className="lp-stats__hint">{assignPct}%</span>
          </div>
        </div>

        <div className="lp-stats__card">
          <div className="lp-stats__icon-wrap lp-stats__icon-wrap--blue">
            <span>💻</span>
          </div>
          <div className="lp-stats__body">
            <span className="lp-stats__label">Challenges Solved</span>
            <span className="lp-stats__num">
              {stats.doneCoding} / {stats.totalCoding}
            </span>
            <div className="lp-stats__bar">
              <div
                className="lp-stats__fill lp-stats__fill--blue"
                style={{ width: `${codingPct}%` }}
              />
            </div>
            <span className="lp-stats__hint">{codingPct}%</span>
          </div>
        </div>
      </div>

      {/* ── 3-column workspace ──────────────────────────────────────── */}
      <div className="lp-workspace">
        <div className="lp-workspace__nav">
          <SessionNavTree
            modules={data.modules}
            selectedSessionId={selectedSession?._id}
            onSelectSession={handleSelectSession}
          />
        </div>

        <div className="lp-workspace__main">
          <InlineSessionDetail
            sessionId={selectedSession?._id || null}
            sessionState={selectedSession?.student?.state}
            selectedSession={selectedSession}
            aivoOnly={aivoOnly}
          />
        </div>

        <div className="lp-workspace__sidebar">
          <LearningSidebar
            modules={data.modules}
            summary={data.summary}
            gamification={gamification}
            selectedSession={selectedSession}
            onOpenSession={handleSelectSession}
          />
        </div>
      </div>
    </div>
  );
};

export default LearningPathView;
